import type { NavigationLink } from '~/gql/navigation.gql'
import type { LinkBlock } from '~/shared/types'

const pattern = new RegExp(/^\/[a-z]{2}-([a-z]{2})\/?/)

export function useUrlBuilder() {
  function slugToPagePath(slug: string) {
    if (!slug) return ''
    return slug.replace(/__/g, '/').replace(/^\/+/, '')
  }

  function resolveLocalePath(to: string): string {
    to = (to || '').replace(/\/+$/, '')

    const { $i18n } = useNuxtApp()

    if (to.startsWith('https://') || to.startsWith('http://')) return to

    // If the path is already locale prefixed, return
    if (pattern.test(to)) {
      return to
    }

    // Construct localised path removing any leading or trailing slashes from the 'to' param
    return ('/' + $i18n.locale.value + '/' + to.replace(/^\/+/, '')).replace(/\/+$/, '')
  }

  function buildInternalLink(item: LinkBlock | NavigationLink) {
    function isLinkBlock(item: LinkBlock | NavigationLink): item is LinkBlock {
      return !!item && 'modalSidebar' in item
    }

    if (!item?.internalLink) {
      return undefined
    }

    const linkQueryParams = item?.internalLinkQueryParams ? item.internalLinkQueryParams : ''
    let pathPrefix = ''

    if (!item?.internalLink && isLinkBlock(item)) {
      if (!item?.modalSidebar) {
        return undefined
      }

      return '?modal=' + item.modalSidebar.slug
    }

    if (item?.internalLink) {
      switch (item.internalLink._modelApiKey) {
        case 'composite_pdp':
          pathPrefix = 'shop/'
          break
        case 'article_category':
          pathPrefix = 'explore/'
          break
        case 'article':
          if (isLinkBlock(item) && item.internalLink.category?.slug) {
            pathPrefix = 'explore/' + item.internalLink.category?.slug + '/'
          }
          break
        case 'athlete':
          pathPrefix = 'explore/pro-team/'
          break
        default:
          pathPrefix = ''
      }
    }

    return resolveLocalePath(pathPrefix + slugToPagePath(item.internalLink.slug)) + linkQueryParams
  }

  function isLinkOrOpenUI(item: LinkBlock) {
    if (item?.internalLink)
      return {
        isLink: true,
        opensUI: false,
        internalLink: buildInternalLink(item),
      }
    else if (item?.modalSidebar?.slug) return { isLink: false, opensUI: true, internalLink: '' }

    return { isLink: false, opensUI: false, internalLink: '' }
  }

  function openUIElement(item: LinkBlock) {
    const slug = item?.modalSidebar?.slug
    if (slug) {
      switch (item.modalSidebar?.type) {
        case 'sidebar_page': {
          const sidebarStore = useSidebarStore()
          sidebarStore.setActiveSidebar(slug)
          break
        }
        case 'dialog': {
          const modalStore = useModalStore()
          modalStore.OPEN_MODAL_PAGE({ slug })
          break
        }
      }
    } else console.error('Cannot open modal or sidebar with no slug')
  }

  return { resolveLocalePath, slugToPagePath, buildInternalLink, isLinkOrOpenUI, openUIElement }
}
